import React, { type ComponentType, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import {
  flip,
  FloatingNode,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Box, type SvgIconProps, Typography } from '@mui/material';

interface EditablePropertyRowProps {
  icon: ComponentType<SvgIconProps>;
  label: string;
  /** Display node — what shows when not editing */
  value: React.ReactNode;
  /** Editor renderer — receives `onClose` to commit-and-close from inside */
  editor: (props: { onClose: () => void }) => React.ReactNode;
  /** Optional fixed width for the popover (defaults to fit content) */
  popoverWidth?: number | string;
}

/**
 * Linear-style click-to-edit row. Click anywhere on the row to open a small
 * Floating UI popover containing the editor. Escape and outside-click dismiss.
 *
 * When rendered inside a `<FloatingTree>` (e.g. inside another hover popover),
 * the row registers itself as a child node so that the parent's `useDismiss`
 * and `safePolygon` recognize this popover as part of the tree — clicks and
 * hovers inside the editor don't accidentally close the parent.
 */
export const EditablePropertyRow = ({
  icon: Icon,
  label,
  value,
  editor,
  popoverWidth,
}: EditablePropertyRowProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Tree coordination for nested-popover scenarios (e.g. inside the calendar
  // hover popover). Outside a `<FloatingTree>`, both hooks return undefined
  // and the popover behaves as a standalone floating element.
  const parentId = useFloatingParentNodeId();
  const nodeId = useFloatingNodeId();

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const content = (
    <>
      <Box
        ref={refs.setReference}
        {...getReferenceProps()}
        sx={{
          display: 'grid',
          gridTemplateColumns: '120px 1fr',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          px: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'background-color 0.12s',
          ...(isOpen
            ? { bgcolor: 'rgba(0,0,0,0.04)' }
            : { '&:hover': { bgcolor: 'rgba(0,0,0,0.025)' } }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: ELIG_TOKENS.inkSoft }}>
          <Icon sx={{ fontSize: 14 }} />
          <Typography
            variant="body2"
            sx={{ fontSize: 12, fontWeight: 500, color: ELIG_TOKENS.inkSoft }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>{value}</Box>
      </Box>

      {isOpen && (
        <FloatingPortal>
          <Box
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1400 }}
            {...getFloatingProps()}
          >
            <Box
              sx={{
                bgcolor: ELIG_TOKENS.card,
                borderRadius: 2,
                border: `1px solid ${ELIG_TOKENS.lineStrong}`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                ...(popoverWidth ? { width: popoverWidth } : { minWidth: 200 }),
              }}
            >
              {editor({ onClose: () => setIsOpen(false) })}
            </Box>
          </Box>
        </FloatingPortal>
      )}
    </>
  );

  // FloatingNode registers this popover with any ancestor `<FloatingTree>`.
  // Without a tree (drawer usage), `nodeId`/`parentId` are undefined and the
  // node renders inertly — no behavioral change.
  return parentId !== null ? <FloatingNode id={nodeId}>{content}</FloatingNode> : content;
};
